import { CheckIcon } from '@heroicons/react/solid'
import { createContext, useContext, useState } from 'react'
import { ErrorsComponent } from './ErrorsComponent'
import Loader from './Loader'

/**
 * KNOWNISSUES
 * errors shows on every step until its fixed.
 */

const CurrentStepContext = createContext({
    currentStep: 1,
    totalSteps: 2,
})

export function Stepper({ children, config }) {
    return (
        <CurrentStepContext.Provider value={config}>
            <nav aria-label="Progress" className={'md:max-h-full h-auto'}>
                <ol role="list">{children}</ol>
            </nav>
        </CurrentStepContext.Provider>
    )
}

export const StepButtons = ({ onNext, nextButtonText }) => {
    const {
        canGoToPrevStep,
        canGoToNextStep,
        disableNext,
        goToPrevStep,
        nextStep,
        finalButton,
        stickyFooter,
    } = useContext(CurrentStepContext)

    const [loading, setLoading] = useState(false)

    const handleNext = async () => {
        let canProceed = true

        if (onNext) {
            setLoading(true)
            try {
                await onNext()
            } catch (error) {
                canProceed = false
            } finally {
                setLoading(false)
            }
        }

        if (!canProceed) return

        nextStep()
        if (typeof window !== 'undefined') {
            window.requestAnimationFrame(() => {
                window.scrollTo({
                    top: 0,
                    behavior: 'smooth',
                })
            })
        }
    }

    return (
        <>
            {loading && <Loader />}
            <div
                className={`mt-8 flex gap-2
            ${
                !canGoToPrevStep && canGoToNextStep
                    ? 'justify-end'
                    : 'justify-between'
            }
            ${
                stickyFooter
                    ? 'sticky bottom-0 z-10 -mx-4 border-t border-[#ECE6DC] bg-white px-4 py-4 shadow-[0_-8px_20px_rgba(26,29,35,0.08)]'
                    : ''
            }
        `}>
                {canGoToPrevStep && (
                    <button
                        data-button-id="prev-step-button"
                        onClick={goToPrevStep}
                        type="button"
                        className="inline-flex items-center justify-center rounded-full bg-[#EFE9E0] px-[22px] py-3 text-[14.5px] font-semibold text-[#1A1D23] transition-[transform,background-color] duration-150 hover:bg-[#E7E0D5] active:translate-y-[1px] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E7E0D5] focus-visible:ring-offset-2"
                        aria-haspopup="listbox"
                        aria-expanded="true"
                        aria-labelledby="listbox-label">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-[18px] w-[18px]"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth="2">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M7 16l-4-4m0 0l4-4m-4 4h18"
                            />
                        </svg>
                        <span className="ml-2">Back</span>
                    </button>
                )}
                {canGoToNextStep && (
                    <button
                        data-button-id="next-step-button"
                        disabled={disableNext}
                        onClick={handleNext}
                        type="button"
                        className={`inline-flex items-center justify-center rounded-full px-[22px] py-3 text-[14.5px] font-semibold text-white transition-[transform,box-shadow,filter] duration-150 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#F28230]/15 ${
                            disableNext
                                ? 'cursor-not-allowed bg-[#E9DED0] text-white/80 shadow-none'
                                : 'cursor-pointer bg-[linear-gradient(90deg,#ED5C2F,#F28230)] shadow-[0_6px_16px_rgba(237,92,47,0.28)] hover:shadow-[0_10px_22px_rgba(237,92,47,0.34)] active:translate-y-[1px]'
                        }`}
                        aria-haspopup="listbox"
                        aria-expanded="true"
                        aria-labelledby="listbox-label">
                        <span className="mr-2">
                            {nextButtonText ? nextButtonText : 'Next'}
                        </span>
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-[18px] w-[18px]"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth="2">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M17 8l4 4m0 0l-4 4m4-4H3"
                            />
                        </svg>
                    </button>
                )}
                {!canGoToNextStep && finalButton && finalButton}
            </div>
        </>
    )
}

const TitleComponent = ({ title }) => {
    const containsHTML = /<\/?[a-z][\s\S]*>/i.test(title)

    return (
        <div className="block min-w-0 flex-1 text-md font-semibold text-left text-gray-800">
            {containsHTML ? (
                <div dangerouslySetInnerHTML={{ __html: title }} />
            ) : (
                title
            )}

            {/* {index === 1 && router.pathname.includes('/product/') && (
                <SampleLeadListInfo />
            )} */}
        </div>
    )
}

export const Step = ({
    index,
    title,
    auxTitle,
    hiddenContent,
    onNext,
    nextButtonText,
    children,
}) => {
    const { currentStep, totalSteps, errors } = useContext(CurrentStepContext)

    const isCompleted = currentStep > index
    const isCurrent = currentStep === index
    const isLast = index !== totalSteps

    return (
        <>
            <li
                data-step-name={title}
                className={`${!isLast ? 'pb-10' : 'pb-4'} relative`}>
                {isLast && (
                    <div
                        className={`-ml-px absolute mt-0.5 top-3 left-3 w-0.5 h-full
           ${
               isCompleted
                   ? 'bg-olm'
                   : isCurrent
                   ? 'bg-olm opacity-20'
                   : 'bg-olm opacity-20'
           } `}
                    />
                )}
                <div
                    className="relative flex items-start group"
                    aria-current="step">
                    <span className="h-6 flex items-center" aria-hidden="true">
                        <span
                            className={`relative z-10 w-6 h-6 flex items-center justify-center
        rounded-full
        ${
            isCompleted
                ? 'bg-olm '
                : // group-hover:bg-olh
                isCurrent
                ? 'bg-white border-2 border-olm '
                : 'bg-white border-2 border-olm/20 ' /* group-hover:border-olh */
        }`}>
                            {isCompleted ? (
                                <CheckIcon
                                    className="w-5 h-5 text-white"
                                    aria-hidden="true"
                                />
                            ) : isCurrent ? (
                                <span className="h-2.5 w-2.5 bg-olm rounded-full" />
                            ) : (
                                <span className="h-2.5 w-2.5 bg-transparent rounded-full" />
                                // group-hover:bg-olh
                            )}
                        </span>
                    </span>

                    <div className="ml-3 min-w-0 flex flex-col w-full">
                        <div
                            // type="button"
                            className="flex justify-between gap-2">
                            <div className="block min-w-0 flex-1 text-md font-semibold text-left text-gray-800">
                                <TitleComponent title={title} />
                            </div>
                            {auxTitle ? (
                                <span className="ml-auto block flex-shrink-0 whitespace-nowrap text-[12px] leading-6">
                                    <span
                                        className={`font-semibold ${
                                            isCurrent
                                                ? 'text-[#ED5C2F]'
                                                : 'text-[#9AA0A6]'
                                        }`}>
                                        {auxTitle}
                                    </span>
                                </span>
                            ) : (
                                <span
                                    className={`block text-xs leading-6
                                    ${
                                        isCurrent
                                            ? 'font-semibold text-olm'
                                            : 'text-gray-200'
                                    }`}>
                                    {/* {isCompleted
                                        ? 'completed'
                                        : `${index} / ${totalSteps}`} */}
                                </span>
                            )}
                        </div>
                        {isCompleted && hiddenContent}
                        <div
                            className={` ${
                                isCurrent ? 'mt-4 py-4 border-olm' : 'hidden'
                            }
                                ${isLast ? 'border-y-2' : 'border-t-2'}
                            `}>
                            {children}
                            {errors && <ErrorsComponent errors={errors} />}
                            <StepButtons
                                onNext={onNext}
                                nextButtonText={nextButtonText}
                            />
                        </div>
                    </div>
                </div>
            </li>
        </>
    )
}
